<template>
  <div :class="[...containerClasses, $attrs.class]">
    <div class="n-section__components" :style="componentsCssVars">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
const BASE_CLASS = 'n-section';

interface Props {
  headline?: string | null;
  headlineElement?: string;
  gridColumns?: number;
  cssFlags?: string[];
  background?: {
    color?: string;
    mobileImage?: string;
    mobileImageSize?: string;
    desktopImage?: string;
    desktopImageSize?: string;
    desktopImageWidth?: string;
    mobileImageWidth?: string;
  };
  columnsGap?: number[][];
}

const props = withDefaults(defineProps<Props>(), {
  headline: null,
  headlineElement: 'h2',
  gridColumns: 12,
  cssFlags: () => [],
  columnsGap: () => [[20], [40], [60]]
});

defineOptions({
  inheritAttrs: false
});

const getColumnsGap = (bp) => {
  const [sm, md, lg] = props.columnsGap ?? [[20], [40], [60]];
  const bps = { sm, md, lg };
  return `${bps[bp][0]}px ${bps[bp][1] || bps[bp][0]}px`;
};

const componentsCssVars = computed(() => {
  const cssVars = {
    '--columns': props.gridColumns,
    '--columns-gap-sm': getColumnsGap('sm'),
    '--columns-gap-md': getColumnsGap('md'),
    '--columns-gap-lg': getColumnsGap('lg'),
    '--background-color': (props.background?.color ?? 'transparent') || 'transparent'
  };

  if (String(props.background?.mobileImage ?? '').length) {
    cssVars['--background-image-mobile'] = `url(${props.background.mobileImage})`;
    cssVars['--background-image-mobile-size'] = props.background.mobileImageSize || 'cover';
  }

  if (String(props.background?.desktopImage ?? '').length) {
    cssVars['--background-image-desktop'] = `url(${props.background.desktopImage})`;
    cssVars['--background-image-desktop-size'] = props.background.desktopImageSize || 'cover';
  }
  if (String(props.background?.desktopImageWidth ?? '').length) {
    cssVars['--background-image-desktop-width'] = `${props.background.desktopImageWidth}px` || '100%';
  }
  if (String(props.background?.mobileImageWidth ?? '').length) {
    cssVars['--background-image-mobile-width'] = `${props.background.mobileImageWidth}px` || '100%';
  }
  return cssVars;
});

const containerClasses = computed(() => {
  const classes = [BASE_CLASS, ...props.cssFlags.map((f) => `${BASE_CLASS}--${f}`)];
  if (String(props.background?.mobileImage ?? '').length) {
    classes.push(`${BASE_CLASS}--background-image-mobile`);
  }
  if (String(props.background?.desktopImage ?? '').length) {
    classes.push(`${BASE_CLASS}--background-image-desktop`);
  }
  return classes;
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_box.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_position.scss' as *;

.n-section {
  --bottom-spacing: 80px;
  --max-width: calc(100vw - 40px);
  width: 100%;
  max-width: var(--max-width);
  position: relative;
  margin: auto;

  @include bp(lg) {
    --max-width: var(--container-lg);
    --bottom-spacing: 160px;
  }

  &--no-divider {
    --bottom-spacing: 40px;
    @media only screen and (min-width: 45rem) {
      --bottom-spacing: 80px;
    }
  }

  &--fluid {
    --max-width: 100%;
    --padding: 0;

    @media only screen and (min-width: 45rem) {
      --max-width: 100%;
    }
  }

  &:not(:last-child) {
    margin-bottom: var(--bottom-spacing);

    @include bp(md) {
      margin-bottom: var(--bottom-spacing);
    }

    &:not(.n-section--no-divider):after {
      @include pseudo-box(100%, 2px);
      @include absolute(auto, auto, calc(-1 * var(--bottom-spacing) / 2), auto);
      height: 1px;
      border-radius: 8px;
      background-color: #ccc;
    }
  }
  &__components {
    background-color: var(--background-color);

    display: grid;
    grid-template-columns: repeat(var(--columns, 1), minmax(auto, 1fr));
    gap: var(--columns-gap-sm, 20px 20px);
    max-width: 100%;
    width: var(--background-image-mobile-width);

    @include bp(sm-up) {
      width: var(--background-image-desktop-width);
    }

    @include bp(md) {
      gap: var(--columns-gap-md, 20px 20px);
    }
    @include bp(lg) {
      gap: var(--columns-gap-lg, 20px 20px);
    }

    .n-section--justify-center & {
      justify-items: center;
    }
  }

  &--background-image-mobile {
    .n-section__components {
      background-image: var(--background-image-mobile);
      background-size: var(--background-image-mobile-size);
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  &--background-image-desktop {
    .n-section__components {
      margin: auto;
      @include bp(sm-up) {
        background-image: var(--background-image-desktop);
        background-size: var(--background-image-desktop-size);
        background-position: center;
        background-repeat: no-repeat;
      }
    }
  }

  &--light-shadow {
    border: 2px solid #eeeeee;
    box-shadow: 0px 16px 36px rgba(0, 0, 0, 0.06);
    border-radius: 24px;
    padding: 20px;

    @include bp(md) {
      padding: 40px;
    }
  }
}
</style>
